/**
 * 🎠 SupportersCarousel — PATCH-309 (2026-02)
 * ────────────────────────────────────────────────────────────
 * Auto-scrolling "Our Supporters" ribbon that sits on the landing
 * page. Pulls the same public payload as /supporters, dedupes the
 * hidden empty states, and infinite-loops via CSS keyframes.
 *
 * Design notes:
 *   • Duplicates the roster inline so the marquee wraps
 *     seamlessly.
 *   • Pauses on hover so a curious visitor can read a name.
 *   • Renders NOTHING when there are zero entries — no "no
 *     supporters" placeholder pollutes the landing page.
 */
import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { Heart, ArrowRight } from "lucide-react";

const API = (process.env.REACT_APP_BACKEND_URL || "") + "/api/site";
const MEDIA_BASE = process.env.REACT_APP_BACKEND_URL || "";

export default function SupportersCarousel() {
  const [items, setItems] = useState([]);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await axios.get(`${API}/supporters`);
        if (cancelled) return;
        // Flatten across categories, keep only the visible ones, and
        // put featured entries first so the marquee opens strong.
        const flat = [];
        for (const cat of data.categories || []) {
          const rows = (data.grouped?.[cat.key] || []);
          for (const r of rows) flat.push({ ...r, __cat: cat.label });
        }
        flat.sort((a, b) => (b.featured ? 1 : 0) - (a.featured ? 1 : 0));
        setItems(flat);
      } catch {
        // Silent — landing page must never fail if the CMS is empty.
        setItems([]);
      } finally {
        if (!cancelled) setLoaded(true);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  // Duplicate the list so the -50% marquee wraps without a gap.
  const rail = useMemo(() => [...items, ...items], [items]);

  if (!loaded || items.length === 0) return null;

  return (
    <section
      data-testid="supporters-carousel"
      className="relative bg-[#0A0612] text-white py-10 overflow-hidden"
    >
      <style>{`
        @keyframes tea-supporters-scroll {
          from { transform: translateX(0); }
          to   { transform: translateX(-50%); }
        }
        .tea-supporters-rail {
          animation: tea-supporters-scroll 45s linear infinite;
        }
        .tea-supporters-rail:hover { animation-play-state: paused; }
      `}</style>

      <div className="px-6 lg:px-10 max-w-7xl mx-auto mb-6 flex items-end justify-between gap-4">
        <div>
          <div className="flex items-center gap-2 mb-2">
            <Heart className="h-4 w-4 text-[#C9A227]" />
            <p className="font-mono text-[10px] tracking-[0.28em] uppercase text-[#C9A227]">
              Our Supporters
            </p>
          </div>
          <h2 className="text-2xl sm:text-3xl font-display font-bold tracking-tight">
            The community <span className="text-[#C9A227]">making this possible.</span>
          </h2>
        </div>
        <Link
          to="/supporters"
          data-testid="supporters-carousel-cta"
          className="hidden sm:inline-flex items-center gap-2 font-mono text-[10px] tracking-[0.22em] uppercase text-[#F4D88A] hover:text-white transition-colors"
        >
          See everyone <ArrowRight className="h-3.5 w-3.5" />
        </Link>
      </div>

      <div className="relative w-full">
        {/* Left + right fade masks so items enter/exit gracefully. */}
        <div className="pointer-events-none absolute left-0 top-0 bottom-0 w-24 z-10
                        bg-gradient-to-r from-[#0A0612] to-transparent" />
        <div className="pointer-events-none absolute right-0 top-0 bottom-0 w-24 z-10
                        bg-gradient-to-l from-[#0A0612] to-transparent" />

        <div className="flex tea-supporters-rail" style={{ width: "max-content" }}>
          {rail.map((it, idx) => (
            <RailCard key={`${it.id}-${idx}`} item={it} />
          ))}
        </div>
      </div>

      <div className="sm:hidden text-center mt-6">
        <Link
          to="/supporters"
          className="inline-flex items-center gap-2 font-mono text-[10px] tracking-[0.22em] uppercase text-[#F4D88A]"
        >
          See everyone <ArrowRight className="h-3.5 w-3.5" />
        </Link>
      </div>
    </section>
  );
}


function RailCard({ item }) {
  const src = `${MEDIA_BASE}${item.media_url}`;
  return (
    <div
      className="mx-3 shrink-0 w-32 sm:w-40 group"
      data-testid={`supporters-carousel-item-${item.id}`}
    >
      <div className="aspect-square bg-white/5 overflow-hidden border border-white/10 group-hover:border-[#C9A227] transition-colors">
        {item.media_kind === "video" ? (
          <video src={src} muted loop autoPlay playsInline
                 className="w-full h-full object-cover" />
        ) : (
          <img src={src} alt={item.name} loading="lazy"
               className="w-full h-full object-cover" />
        )}
      </div>
      <div className="mt-2">
        <p className="font-display text-[11px] font-semibold text-white truncate" title={item.name}>
          {item.name}
        </p>
        <p className="font-mono text-[8px] tracking-[0.22em] uppercase text-[#C9A227]/80 truncate">
          {item.__cat}
        </p>
      </div>
    </div>
  );
}
